/**
 * Copy for the first-login wizard. Same flat-key convention as the tour copy
 * (`src/features/onboarding/tour-copy.ts`): Indonesian is authored first, then
 * zh and en with the same keys (src/constants/translations.parity.test.ts).
 *
 * Callers resolve at render, so switching the language on step 0 re-renders
 * every string on screen.
 */
import { DEFAULT_LANGUAGE, type LanguageCode } from '@constants/translations';

type CopyDict = Record<string, string>;

const id: CopyDict = {
  'rail.welcome': 'Selamat datang',

  'rail.note.profil': 'Data ini hanya dipakai tim Jarvis untuk menghubungi Anda selama masa uji coba.',
  'step.profil.title': 'Data Anda',
  'step.profil.sub': 'Nama, perusahaan, kontak',
  'profil.heading': 'Kenalan dulu, yuk',
  'profil.lead':
    'Isi data singkat ini agar tim Jarvis bisa membantu Anda selama uji coba. Kolom bertanda * wajib diisi.',
  'profil.field.name': 'Nama lengkap',
  'profil.field.position': 'Jabatan',
  'profil.field.company': 'Nama perusahaan / PPJK',
  'profil.field.email': 'Email kantor',
  'profil.field.phone': 'Nomor WhatsApp',
  'profil.field.volume': 'Perkiraan jumlah dokumen per bulan',
  'profil.field.interests': 'Yang ingin Anda coba',
  'profil.placeholder.name': 'Nama Anda',
  'profil.placeholder.position': 'Misalnya Operations Manager',
  'profil.placeholder.company': 'PT ...',
  'profil.placeholder.email': 'nama@perusahaan.co.id',
  'profil.placeholder.phone': '0812-3456-7890',
  'profil.volume.lt50': 'Di bawah 50',
  'profil.volume.50-200': '50 – 200',
  'profil.volume.gt200': 'Di atas 200',
  'profil.interest.PIB': 'PIB (impor)',
  'profil.interest.PEB': 'PEB (ekspor)',
  'profil.interest.MANIFEST': 'Manifest',
  'profil.interest.TRACKING': 'Track & Trace',
  'profil.interest.OTHER': 'Lainnya',
  'profil.error.required': 'Wajib diisi.',
  'profil.error.email': 'Format email belum benar.',
  'profil.error.phone': 'Nomor harus berisi 8 sampai 15 angka.',
  'profil.error.save': 'Data belum tersimpan. Coba lagi.',
  'rail.note.default': 'Semua langkah bisa dilewati. Anda bisa mengisinya kapan saja dari menu Master.',
  'rail.note.appearance': 'Semua langkah bisa dilewati. Tampilan bisa diubah kapan saja di Pengaturan.',
  'rail.note.master': 'Semua langkah bisa dilewati. Data PPJK bisa dilihat dan diubah di menu Master.',
  'rail.note.private': 'Data ini hanya dipakai oleh PPJK Anda dan tidak dibagikan ke pihak lain.',
  'rail.note.consignee':
    'Data consignee hanya bisa dilihat oleh pengguna PPJK Anda. Tidak dibagikan ke PPJK lain dan hanya dipakai untuk mengisi dokumen Anda.',

  'step.tampilan.title': 'Tampilan',
  'step.tampilan.sub': 'Tema, bahasa, ukuran teks',
  'step.pernyataan.title': 'Pernyataan',
  'step.pernyataan.sub': 'Penanda tangan dokumen',
  'step.consignee.title': 'Data Consignee',
  'step.consignee.sub': 'Unggah dari Excel',
  'step.film.filledNow.title': 'Di mana mengubahnya',
  'step.film.teamFilled.title': 'Data PPJK sudah siap',
  'step.film.masterInfo.title': 'Semua ada di menu Master',
  'step.film.appearance.title': 'Atur tampilan',
  'step.film.sub': 'Tur singkat',

  'tampilan.heading': 'Atur tampilan sesuai kenyamanan Anda',
  'tampilan.lead': 'Pilihan ini hanya berlaku untuk akun Anda dan bisa diubah kapan saja di Pengaturan.',

  'pernyataan.heading': 'Siapa yang menandatangani dokumen PIB/PEB Anda?',
  'pernyataan.lead': 'Diisi otomatis ke bagian Pernyataan setiap dokumen baru. Bisa diubah per dokumen.',
  'pernyataan.field.place': 'Tempat (kota)',
  'pernyataan.field.name': 'Nama penanda tangan',
  'pernyataan.field.position': 'Jabatan',
  'pernyataan.placeholder.place': 'JAKARTA',
  'pernyataan.placeholder.name': 'Nama sesuai dokumen',
  'pernyataan.placeholder.position': 'Misalnya DIREKTUR',
  'pernyataan.note.title': 'Dipakai bersama satu PPJK.',
  'pernyataan.note.body':
    'Jika rekan Anda mengisinya lebih dulu, langkah ini tidak akan muncul lagi. Nilai terakhir yang disimpan yang dipakai.',
  'pernyataan.required': 'Isi ketiga kolom untuk menyimpan, atau lewati dulu.',
  'pernyataan.saveFailed': 'Pernyataan belum tersimpan. Coba lagi atau lewati dulu.',
  'pernyataan.utamaHint': 'Disimpan sebagai Pernyataan utama. Pernyataan lain bisa ditambahkan di Master › Pernyataan.',

  'consignee.heading': 'Daftar consignee Anda',
  'consignee.lead': 'Isi sekali, pakai berulang. Kosongkan yang tidak tahu, hanya Nama dan NPWP yang wajib.',
  'consignee.card.template.title': '1. Unduh template',
  'consignee.card.template.body': 'Excel dengan kolom siap isi, contoh baris, dan pilihan dropdown.',
  'consignee.card.template.button': 'Unduh Template_Consignee_JARVIS.xlsx',
  'consignee.card.upload.title': '2. Unggah yang sudah diisi',
  'consignee.downloadFailed': 'Template gagal diunduh. Coba lagi.',
  'consignee.imported': '{{n}} consignee tersimpan. Anda bisa lanjut.',

  'film.filledNow.heading': 'Di mana mengubahnya nanti',
  'film.filledNow.lead': 'Semua yang Anda isi tadi ada di menu Master, dan tampilan ada di Pengaturan.',
  'film.filledNow.caption':
    'Sidebar, lalu Master, lalu Pernyataan dan Data Consignee. Tampilan bisa diubah lagi di Pengaturan.',
  'film.teamFilled.heading': 'Data PPJK Anda sudah siap',
  'film.teamFilled.lead':
    'Tim Anda sudah mengisi Pernyataan dan Data Consignee. Anda bisa melihat dan mengubahnya di menu Master, dan mengatur tampilan di Pengaturan.',
  'film.teamFilled.caption':
    'Sidebar, lalu Master, lalu Pernyataan dan Data Consignee. Tampilan bisa diubah lagi di Pengaturan.',
  'film.masterInfo.heading': 'Semua ada di menu Master',
  'film.masterInfo.lead':
    'Pernyataan dan Data Consignee PPJK Anda bisa dilihat dan diubah di menu Master, dan tampilan diatur di Pengaturan.',
  'film.masterInfo.caption':
    'Sidebar, lalu Master, lalu Pernyataan dan Data Consignee. Tampilan bisa diubah lagi di Pengaturan.',
  'film.appearance.heading': 'Atur tampilan kapan saja',
  'film.appearance.lead': 'Tema, bahasa, dan ukuran teks bisa diubah lagi di Pengaturan.',
  'film.appearance.caption': 'Sidebar, lalu Pengaturan di bagian bawah.',
  'film.replay': 'Putar ulang',

  'colleague.statement.title': 'Pernyataan sudah diisi oleh rekan Anda',
  'colleague.statement.keep': 'Tetap ubah',
  'colleague.consignee.title': 'Data Consignee sudah diisi oleh rekan Anda ({{n}} data)',
  'colleague.consignee.keep': 'Tetap unggah',
  'colleague.continue': 'Lanjut',

  'footer.later': 'Lanjutkan nanti',
  'footer.back': 'Kembali',
  'footer.skip': 'Lewati',
  'footer.saveNext': 'Simpan & lanjut',
  'footer.next': 'Lanjut',
  'footer.finish': 'Selesai',
  'footer.stepOf': 'Langkah {{current}} dari {{total}}',
};

const zh: CopyDict = {
  'rail.welcome': '欢迎',

  'rail.note.profil': '这些信息只供 Jarvis 团队在试用期间与您联系。',
  'step.profil.title': '您的信息',
  'step.profil.sub': '姓名、公司、联系方式',
  'profil.heading': '先认识一下',
  'profil.lead': '请填写以下简短信息，方便 Jarvis 团队在试用期间协助您。带 * 的为必填项。',
  'profil.field.name': '全名',
  'profil.field.position': '职位',
  'profil.field.company': '公司 / PPJK 名称',
  'profil.field.email': '工作邮箱',
  'profil.field.phone': 'WhatsApp 号码',
  'profil.field.volume': '每月单证数量（约）',
  'profil.field.interests': '想试用的功能',
  'profil.placeholder.name': '您的姓名',
  'profil.placeholder.position': '例如 运营经理',
  'profil.placeholder.company': 'PT ...',
  'profil.placeholder.email': 'name@company.co.id',
  'profil.placeholder.phone': '0812-3456-7890',
  'profil.volume.lt50': '50 以下',
  'profil.volume.50-200': '50 – 200',
  'profil.volume.gt200': '200 以上',
  'profil.interest.PIB': 'PIB（进口）',
  'profil.interest.PEB': 'PEB（出口）',
  'profil.interest.MANIFEST': '舱单',
  'profil.interest.TRACKING': '货物追踪',
  'profil.interest.OTHER': '其他',
  'profil.error.required': '必填。',
  'profil.error.email': '邮箱格式不正确。',
  'profil.error.phone': '号码需为 8 到 15 位数字。',
  'profil.error.save': '尚未保存，请重试。',
  'rail.note.default': '每一步都可以跳过。之后随时可以在 Master 菜单里补填。',
  'rail.note.appearance': '每一步都可以跳过。外观随时可以在“设置”里修改。',
  'rail.note.master': '每一步都可以跳过。PPJK 数据可以在 Master 菜单中查看和修改。',
  'rail.note.private': '这些数据只供您的 PPJK 使用，不会与任何第三方共享。',
  'rail.note.consignee': '收货人数据只有您 PPJK 的用户可见。不会共享给其他 PPJK，只用于填写您的单证。',

  'step.tampilan.title': '外观',
  'step.tampilan.sub': '主题、语言、字号',
  'step.pernyataan.title': '声明 (Pernyataan)',
  'step.pernyataan.sub': '单证签署人',
  'step.consignee.title': '收货人数据',
  'step.consignee.sub': '从 Excel 上传',
  'step.film.filledNow.title': '之后在哪里修改',
  'step.film.teamFilled.title': 'PPJK 数据已就绪',
  'step.film.masterInfo.title': '都在 Master 菜单里',
  'step.film.appearance.title': '调整外观',
  'step.film.sub': '简短导览',

  'tampilan.heading': '把界面调到您看着舒服',
  'tampilan.lead': '这些选择只影响您的账号，随时可以在“设置”里更改。',

  'pernyataan.heading': '谁来签署您的 PIB/PEB 单证？',
  'pernyataan.lead': '会自动填入每份新单证的 Pernyataan 栏，也可以按单证单独修改。',
  'pernyataan.field.place': '地点（城市）',
  'pernyataan.field.name': '签署人姓名',
  'pernyataan.field.position': '职务',
  'pernyataan.placeholder.place': 'JAKARTA',
  'pernyataan.placeholder.name': '与单证一致的姓名',
  'pernyataan.placeholder.position': '例如 DIREKTUR',
  'pernyataan.note.title': '整个 PPJK 共用。',
  'pernyataan.note.body': '如果同事已经先填好，这一步就不会再出现。以最后一次保存的值为准。',
  'pernyataan.required': '三项都填好才能保存，或者先跳过。',
  'pernyataan.saveFailed': '声明尚未保存。请重试，或先跳过。',
  'pernyataan.utamaHint': '保存为默认声明。其他声明可在 主数据 › 声明 中添加。',

  'consignee.heading': '您的收货人名单',
  'consignee.lead': '填一次，反复用。不清楚的留空，只有名称和 NPWP 必填。',
  'consignee.card.template.title': '1. 下载模板',
  'consignee.card.template.body': 'Excel 已备好列名、示例行和下拉选项。',
  'consignee.card.template.button': '下载 Template_Consignee_JARVIS.xlsx',
  'consignee.card.upload.title': '2. 上传填好的文件',
  'consignee.downloadFailed': '模板下载失败，请重试。',
  'consignee.imported': '已保存 {{n}} 个收货人。可以继续。',

  'film.filledNow.heading': '之后在哪里修改',
  'film.filledNow.lead': '刚才填的内容都在 Master 菜单里，外观设置在“设置”里。',
  'film.filledNow.caption': '侧边栏，然后 Master，然后 Pernyataan 和收货人数据。外观随时可以在“设置”里再改。',
  'film.teamFilled.heading': '您的 PPJK 数据已就绪',
  'film.teamFilled.lead':
    '您的团队已经填好 Pernyataan 和收货人数据。您可以在 Master 菜单中查看和修改，并在“设置”里调整外观。',
  'film.teamFilled.caption': '侧边栏，然后 Master，然后 Pernyataan 和收货人数据。外观随时可以在“设置”里再改。',
  'film.masterInfo.heading': '都在 Master 菜单里',
  'film.masterInfo.lead': '您 PPJK 的 Pernyataan 和收货人数据可以在 Master 菜单中查看和修改，外观在“设置”里调整。',
  'film.masterInfo.caption': '侧边栏，然后 Master，然后 Pernyataan 和收货人数据。外观随时可以在“设置”里再改。',
  'film.appearance.heading': '随时调整外观',
  'film.appearance.lead': '主题、语言和字号随时可以在“设置”里再改。',
  'film.appearance.caption': '侧边栏，然后底部的“设置”。',
  'film.replay': '重播',

  'colleague.statement.title': '您的同事已经填好了声明 (Pernyataan)',
  'colleague.statement.keep': '仍要修改',
  'colleague.consignee.title': '您的同事已经填好了收货人数据（{{n}} 条）',
  'colleague.consignee.keep': '仍要上传',
  'colleague.continue': '继续',

  'footer.later': '稍后继续',
  'footer.back': '上一步',
  'footer.skip': '跳过',
  'footer.saveNext': '保存并继续',
  'footer.next': '继续',
  'footer.finish': '完成',
  'footer.stepOf': '第 {{current}} 步，共 {{total}} 步',
};

const en: CopyDict = {
  'rail.welcome': 'Welcome',
  'rail.note.profil': 'The Jarvis team uses this only to contact you during the trial.',
  'step.profil.title': 'About you',
  'step.profil.sub': 'Name, company, contact',
  'profil.heading': "Let's get to know you",
  'profil.lead':
    'Fill in these quick details so the Jarvis team can help you during the trial. Fields marked * are required.',
  'profil.field.name': 'Full name',
  'profil.field.position': 'Job title',
  'profil.field.company': 'Company / PPJK name',
  'profil.field.email': 'Work email',
  'profil.field.phone': 'WhatsApp number',
  'profil.field.volume': 'Estimated documents per month',
  'profil.field.interests': "What you'd like to try",
  'profil.placeholder.name': 'Your name',
  'profil.placeholder.position': 'e.g. Operations Manager',
  'profil.placeholder.company': 'PT ...',
  'profil.placeholder.email': 'name@company.co.id',
  'profil.placeholder.phone': '0812-3456-7890',
  'profil.volume.lt50': 'Under 50',
  'profil.volume.50-200': '50 – 200',
  'profil.volume.gt200': 'Over 200',
  'profil.interest.PIB': 'PIB (import)',
  'profil.interest.PEB': 'PEB (export)',
  'profil.interest.MANIFEST': 'Manifest',
  'profil.interest.TRACKING': 'Track & Trace',
  'profil.interest.OTHER': 'Other',
  'profil.error.required': 'Required.',
  'profil.error.email': "Email format isn't valid.",
  'profil.error.phone': 'The number must have 8 to 15 digits.',
  'profil.error.save': "Couldn't save your details. Please try again.",
  'rail.note.default': 'You can skip any step. You can fill it in any time from the Master Data menu.',
  'rail.note.appearance': 'You can skip any step. You can change the appearance any time in Settings.',
  'rail.note.master': 'You can skip any step. You can view and edit your PPJK data in the Master Data menu.',
  'rail.note.private': 'This data is used only by your PPJK and is never shared with anyone else.',
  'rail.note.consignee':
    "Consignee data is visible only to users in your PPJK. It isn't shared with other PPJKs and is used only to fill in your documents.",
  'step.tampilan.title': 'Appearance',
  'step.tampilan.sub': 'Theme, language, text size',
  'step.pernyataan.title': 'Statement',
  'step.pernyataan.sub': 'Document signatory',
  'step.consignee.title': 'Consignee Data',
  'step.consignee.sub': 'Upload from Excel',
  'step.film.filledNow.title': 'Where to change it',
  'step.film.teamFilled.title': 'Your PPJK data is ready',
  'step.film.masterInfo.title': "It's all in the Master Data menu",
  'step.film.appearance.title': 'Adjust the appearance',
  'step.film.sub': 'Quick tour',
  'tampilan.heading': 'Set up the display the way you like it',
  'tampilan.lead': 'These choices apply only to your account and can be changed any time in Settings.',
  'pernyataan.heading': 'Who signs your PIB/PEB documents?',
  'pernyataan.lead':
    'Filled in automatically in the Statement section of every new document. You can change it per document.',
  'pernyataan.field.place': 'Place (city)',
  'pernyataan.field.name': 'Signatory name',
  'pernyataan.field.position': 'Position',
  'pernyataan.placeholder.place': 'JAKARTA',
  'pernyataan.placeholder.name': 'Name as on the documents',
  'pernyataan.placeholder.position': 'e.g. DIRECTOR',
  'pernyataan.note.title': 'Shared across your whole PPJK.',
  'pernyataan.note.body':
    "If a colleague fills it in first, this step won't appear again. The most recently saved value is used.",
  'pernyataan.required': 'Fill in all three fields to save, or skip for now.',
  'pernyataan.saveFailed': "The statement couldn't be saved. Try again or skip for now.",
  'pernyataan.utamaHint': 'Saved as the primary statement. You can add more statements in Master Data › Statement.',
  'consignee.heading': 'Your consignee list',
  'consignee.lead':
    "Fill it in once, reuse it every time. Leave blank what you don't know — only Name and NPWP are required.",
  'consignee.card.template.title': '1. Download the template',
  'consignee.card.template.body': 'An Excel file with ready-to-fill columns, sample rows, and dropdown options.',
  'consignee.card.template.button': 'Download Template_Consignee_JARVIS.xlsx',
  'consignee.card.upload.title': '2. Upload the completed file',
  'consignee.downloadFailed': "The template couldn't be downloaded. Please try again.",
  'consignee.imported': '{{n}} consignees saved. You can continue.',
  'film.filledNow.heading': 'Where to change it later',
  'film.filledNow.lead': 'Everything you just filled in is in the Master Data menu, and the appearance is in Settings.',
  'film.filledNow.caption':
    'Sidebar, then Master Data, then Statement and Consignee Data. You can change the appearance again in Settings.',
  'film.teamFilled.heading': 'Your PPJK data is ready',
  'film.teamFilled.lead':
    'Your team has already filled in the Statement and Consignee Data. You can view and edit them in the Master Data menu, and adjust the appearance in Settings.',
  'film.teamFilled.caption':
    'Sidebar, then Master Data, then Statement and Consignee Data. You can change the appearance again in Settings.',
  'film.masterInfo.heading': "It's all in the Master Data menu",
  'film.masterInfo.lead':
    "You can view and edit your PPJK's Statement and Consignee Data in the Master Data menu, and adjust the appearance in Settings.",
  'film.masterInfo.caption':
    'Sidebar, then Master Data, then Statement and Consignee Data. You can change the appearance again in Settings.',
  'film.appearance.heading': 'Adjust the appearance any time',
  'film.appearance.lead': 'You can change the theme, language, and text size again in Settings.',
  'film.appearance.caption': 'Sidebar, then Settings at the bottom.',
  'film.replay': 'Replay',
  'colleague.statement.title': 'A colleague has already filled in the Statement',
  'colleague.statement.keep': 'Edit anyway',
  'colleague.consignee.title': 'A colleague has already filled in the Consignee Data ({{n}} records)',
  'colleague.consignee.keep': 'Upload anyway',
  'colleague.continue': 'Continue',
  'footer.later': 'Continue later',
  'footer.back': 'Back',
  'footer.skip': 'Skip',
  'footer.saveNext': 'Save & continue',
  'footer.next': 'Next',
  'footer.finish': 'Finish',
  'footer.stepOf': 'Step {{current}} of {{total}}',
};

const FIRST_LOGIN_COPY: Record<LanguageCode, CopyDict> = { id, zh, en };

export const resolveFirstLoginCopy = (
  language: LanguageCode,
  key: string,
  vars?: Record<string, string | number>
): string => {
  const raw = FIRST_LOGIN_COPY[language]?.[key] ?? FIRST_LOGIN_COPY[DEFAULT_LANGUAGE][key] ?? key;
  if (!vars) {
    return raw;
  }
  return Object.entries(vars).reduce((text, [name, value]) => text.split(`{{${name}}}`).join(String(value)), raw);
};

/** Test-only view of the raw dictionaries. */
export const __FIRST_LOGIN_COPY = FIRST_LOGIN_COPY;
